/**
 * Office control to put a job on a tester for online input — dropped into the
 * job's Water & Reporting card. Filing is a licensed act, so the only choices
 * are "waiting on tester" (arms the 36h nudge, reason required) or a
 * city-registration hold (parked, no nag). There is deliberately no
 * "office files it" path.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { flagWaitingOnTester, officeCityHold } from "@/lib/report-input";
import { UserCheck, MapPinOff } from "lucide-react";

export function ReportInputFlag({
    jobId,
    isPortal,
    quiet = false,
}: {
    jobId: string;
    isPortal?: boolean | null;
    /** The calm job page (#1717): the two choices as quiet text links. */
    quiet?: boolean;
}) {
    const qc = useQueryClient();
    const [mode, setMode] = useState<"idle" | "tester" | "city">("idle");
    const [reason, setReason] = useState("");

    const done = (msg: string) => {
        toast.success(msg);
        setMode("idle");
        setReason("");
        void qc.invalidateQueries({ queryKey: ["report-input"] });
        void qc.invalidateQueries({ queryKey: ["job", jobId] });
    };
    const flag = useMutation({
        mutationFn: () => flagWaitingOnTester(jobId, reason),
        onSuccess: () => done("Flagged — the tester was notified."),
        onError: () => toast.error("Couldn't flag that."),
    });
    const city = useMutation({
        mutationFn: () => officeCityHold(jobId, reason),
        onSuccess: () => done("Parked on a city-registration hold."),
        onError: () => toast.error("Couldn't set that hold."),
    });

    // Paper jobs don't get filed online — nothing to put on a tester.
    if (!isPortal) return null;

    if (mode === "idle" && quiet) {
        const lk =
            "rounded-sm bg-transparent p-0 text-[14px] font-medium text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
        return (
            <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1" data-testid="report-input-flag-quiet">
                <button type="button" className={lk} onClick={() => setMode("tester")}>
                    Waiting on tester to input
                </button>
                <button type="button" className={lk} onClick={() => setMode("city")}>
                    City hold
                </button>
            </div>
        );
    }

    if (mode === "idle") {
        return (
            <div className="mt-2 flex flex-wrap gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMode("tester")}
                    title="The outside tester still has to type this test into the city's portal themselves — flag it so the office knows the wait is on them, not us"
                >
                    <UserCheck className="mr-1.5 h-3.5 w-3.5" />
                    Waiting on tester to input
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setMode("city")}
                    title="The city's portal can't accept this filing yet (e.g. the device isn't registered with them) — flag the block so it isn't read as our delay"
                >
                    <MapPinOff className="mr-1.5 h-3.5 w-3.5" />
                    City hold
                </Button>
            </div>
        );
    }

    return (
        <div className="mt-2 flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-2.5">
            <div className="text-xs font-semibold">
                {mode === "tester" ? "Why is this on the tester? (required)" : "What's the city-registration block?"}
            </div>
            <Input
                autoFocus
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                    mode === "tester"
                        ? "e.g. needs to go in VEPO before Friday's deadline"
                        : "e.g. city hasn't approved our registration yet"
                }
            />
            <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setMode("idle")}>
                    Cancel
                </Button>
                <Button
                    size="sm"
                    disabled={!reason.trim() || flag.isPending || city.isPending}
                    onClick={() => (mode === "tester" ? flag.mutate() : city.mutate())}
                >
                    {mode === "tester" ? "Flag & notify tester" : "Park on city hold"}
                </Button>
            </div>
        </div>
    );
}
